/**
 * IIFE entry point — self-mounting bundle for script-tag / GTM injection.
 *
 * The bundle auto-detects the correct backend URL from the CDN hostname it
 * was served from, then resolves the backend launcher flag unless the host
 * supplies an explicit override:
 *
 *   <!-- nonprod (phoebe-stage.phoenix.edu) → phoebe-2.st.uopx.io:8003  -->
 *   <!-- prod    (phoebe.phoenix.edu)        → phoebe-2.uopx.io:8003     -->
 *   <script src="https://phoebe-stage.phoenix.edu/chat-widget/phoebe-chat.global.js"></script>
 *
 * You can still override any value via window.__PHOEBE_CONFIG__ if needed:
 *
 *   <script>
 *     window.__PHOEBE_CONFIG__ = { enabled: true, backendUrl: "https://custom-backend.example.com" };
 *   </script>
 *   <script src="..."></script>
 *
 * The script inserts a <div id="phoebe-chat-root"> after the host page header
 * when possible (falling back to <body>) and mounts <ChatModal> into it. It is
 * completely self-contained — React, ReactDOM, and all dependencies are bundled in.
 */

import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { ChatModal, type ChatModalLayout } from "./components/ChatModal";
import {
  isNonProdCdnScriptSrc,
  resolveBackendUrl,
  shouldAutoEnableGuestBootstrap,
} from "./runtime/socket/backendUrl";
import { resolveLauncherEnabled, resolveLiveChatEnabled, resolveSidePanelEnabled } from "./runtime/launcherFlag";
import type { SocketInitialSessionIdentity } from "./runtime/socket/sessionIdentity";
import type { GuestSessionBootstrapOptions } from "./runtime/socket/types";
import * as analytics from "./analytics";

// Replaced by tsup only in the IIFE artifact. Library builds never import this entry point.
declare const __PHOEBE_IIFE_STRICT_SIDE_PANEL_POLICY__: boolean;

declare global {
  interface Window {
    __PHOEBE_CHAT_ROOT__?: Root;
    /**
     * In-flight guard for the async pre-mount flag check, shared on `window`
     * (not module-scoped) because a duplicate <script> tag injection re-runs
     * this entire IIFE bundle as a fresh, independent closure — a module-level
     * `let` would not be shared between the two executions. See mount()'s
     * doc comment for the race this closes.
    */
    __PHOEBE_MOUNT_IN_FLIGHT__?: boolean;
    /** Backend-scoped launcher decision shared by duplicate bundle injections. */
    __PHOEBE_LAUNCHER_FLAG_PROMISE__?: {
      backendUrl: string | null | undefined;
      promise: Promise<boolean>;
    };
    __PHOEBE_CONFIG__?: {
      enabled?: boolean;
      /** Direct Socket.IO URL for the Phoebe supervisor backend. */
      backendUrl?: string;
      /** Optional initial Socket.IO session identity for local/test harnesses. */
      sessionIdentity?: SocketInitialSessionIdentity;
      /**
       * Segment Analytics.js write key.
       * If provided, the widget loads @segment/analytics-next with this key.
       * If omitted, the widget uses the host page's window.analytics (if present)
       * or falls back to a console-log stub for local development.
       */
      segmentWriteKey?: string;
      /**
       * Set to true to disable all Segment analytics calls entirely.
       * Useful for pages where analytics consent has not been granted.
       */
      disableAnalytics?: boolean;
      /**
       * Set to true to include message body text in Live Chat Message Sent
       * and Live Chat Message Received Segment track events.
       * Defaults to false — message content is not tracked by default.
       */
      includeMessageBody?: boolean;
      /**
       * Ping Identity user-subject JWT for authenticated sessions.
       * Forwarded as `auth.user_token` on the Socket.IO handshake.
       * Takes effect only when `sessionIdentity.userToken` is not already set.
       */
      userToken?: string;
      /** Opt into backend guest bootstrap for custom backends that expose the endpoint. */
      enableGuestBootstrap?: boolean;
      /** Same-origin triage pilot only; disabled by default. */
      silentWidgetAuth?: boolean;
      /** Test/local escape hatch; production anonymous pages should omit this. */
      disableGuestBootstrap?: boolean;
      /**
       * Enable console logging for UOPX DataLayer tracking calls when
       * window.uopxDataLayer is not available. Useful for local development,
       * QA, and staging verification.
       *
       * **Warning:** This logs event data (including text and location)
       * to the browser console. Only enable in non-production environments.
       * Defaults to false.
       */
      debugUOPXTracking?: boolean;
      /**
       * Enables browser-console QA utilities under window.__PHOEBE_DEVTOOLS__.
       * Defaults to true for the sandbox and stage CDN embeds and false
       * everywhere else, so production embeds do not expose test hooks.
       * Set explicitly to override either default.
       */
      enableDevtools?: boolean;
      /**
       * Optional visual shell for local/design harnesses.
       * Defaults to the production launcher/modal behavior. `side-panel`
       * requires sidePanelShellEnabled or the OpenFeature/GrowthBook flag
       * super-phoebe.ui.side-panel.enabled for Phase 2 release gating.
       */
      layout?: ChatModalLayout;
      /**
       * Phase 2 flag fallback for the right-rail side-panel shell.
       * Keep false/omitted for Phase 1 so the original launcher remains active.
       */
      sidePanelShellEnabled?: boolean;
      /**
       * Future enrollment consultation scheduling UX.
       * Defaults to false for Phase 1 because scheduling is not yet available.
       */
      enrollmentConsultationEnabled?: boolean;
      /** Enables Legal-approved canned insights in the thinking state. */
      thinkingInsightsEnabled?: boolean;
      /**
       * Enable or disable the Genesys live chat feature.
       * Defaults to false for Phase 1 because live-agent entry points are not yet available.
       * Overridden by GrowthBook flag evaluation when growthBookClientKey is also provided.
       */
      genesysLiveChatEnabled?: boolean;
      /**
       * Enable or disable the Genesys live chat queue position display.
       * Defaults to false. Overridden by GrowthBook flag evaluation
       * when growthBookClientKey is also provided.
       */
      genesysLiveChatQueueDisplayEnabled?: boolean;
      /**
       * GrowthBook client-side SDK key.
       * When provided, the OpenFeature JS Web SDK is initialised with a
       * custom GrowthBook-backed provider and feature flags are resolved
       * from GrowthBook.
       * If omitted, only the prop-based values or local defaults are used.
       */
      growthBookClientKey?: string;
      /**
       * Optional GrowthBook API host override. Only used when
       * growthBookClientKey is provided.
       */
      growthBookApiHost?: string;
    };
  }
}

function resolveGuestBootstrapOption({
  backendUrl,
  disableGuestBootstrap,
  enableGuestBootstrap,
}: {
  backendUrl: string | undefined;
  disableGuestBootstrap?: boolean;
  enableGuestBootstrap?: boolean;
}): GuestSessionBootstrapOptions | false {
  if (disableGuestBootstrap) return false;
  if (enableGuestBootstrap || shouldAutoEnableGuestBootstrap(backendUrl)) return { enabled: true };
  return false;
}

/**
 * Captured synchronously at parse time — document.currentScript is only
 * available while the script element is being executed, not inside callbacks.
 */
const CURRENT_SCRIPT_SRC: string | undefined = (() => {
  try {
    const currentScript = (document.currentScript as HTMLScriptElement | null)?.src;
    if (currentScript) return currentScript;

    // If a host evaluates the fetched source after currentScript has been
    // cleared, there is no reliable way to identify which of several widget
    // script elements owns this execution. Failing closed avoids selecting a
    // stale script from another embed and routing the request to the wrong
    // environment. Such loaders must provide config.backendUrl explicitly.
    return undefined;
  } catch {
    return undefined;
  }
})();

/** Set __PHOEBE_DEBUG__ = true on window to enable verbose mount logging. */
const DEBUG = typeof window !== "undefined" && (window as unknown as Record<string, unknown>).__PHOEBE_DEBUG__ === true;
const HOST_LAYOUT_OBSERVER_TIMEOUT_MS = 5_000;
const IIFE_STRICT_SIDE_PANEL_POLICY = __PHOEBE_IIFE_STRICT_SIDE_PANEL_POLICY__;

type ChatRootPlacement = "before-main" | "after-header" | "body";

const HOST_HEADER_BOUNDARY_ATTRIBUTE_NAMES = [
  "id",
  "class",
  "data-region",
  "data-testid",
  "data-component",
  "data-cmp",
] as const;
const HOST_HEADER_BOUNDARY_MARKERS = [
  "global-header",
  "globalheader",
  "site-header",
  "siteheader",
  "utility-header",
  "utilityheader",
  "header",
  "masthead",
  "main-nav",
  "mainnav",
  "primary-nav",
  "primarynav",
  "global-nav",
  "globalnav",
  "site-nav",
  "sitenav",
  "utility-nav",
  "utilitynav",
  "navbar",
  "nav-bar",
  "topbar",
  "top-bar",
  "utility-bar",
  "utilitybar",
  "util-bar",
  "utilbar",
] as const;

function hostHeaderBoundaryAttributeMatches(value: string | null | undefined): boolean {
  if (!value) return false;

  const normalized = value.toLowerCase();
  const compact = normalized.replace(/[^a-z0-9]/g, "");
  return HOST_HEADER_BOUNDARY_MARKERS.some((marker) => {
    const markerCompact = marker.replace(/[^a-z0-9]/g, "");
    if (marker === "header") {
      const attributeTokens = normalized.split(/\s+/).filter(Boolean);
      return attributeTokens.includes(marker) || compact === markerCompact;
    }
    return normalized.includes(marker) || compact.includes(markerCompact);
  });
}

function isHostHeaderBoundaryElement(element: Element): boolean {
  const tagName = element.tagName.toLowerCase();
  if (tagName === "header" || tagName === "nav") return true;

  const role = element.getAttribute("role")?.toLowerCase();
  if (role === "banner" || role === "navigation") return true;

  const label = element.getAttribute("aria-label")?.toLowerCase() ?? "";
  if (label.includes("header") || label.includes("navigation")) return true;

  return HOST_HEADER_BOUNDARY_ATTRIBUTE_NAMES.some((attributeName) =>
    hostHeaderBoundaryAttributeMatches(element.getAttribute(attributeName)),
  );
}

function isPhoebeOwnedElement(container: HTMLElement, element: Element): boolean {
  return container.contains(element) || Boolean(element.closest("#phoebe-chat-root, .aui-root"));
}

function firstHostElement(container: HTMLElement, selector: string): Element | null {
  return (
    Array.from(document.body.querySelectorAll(selector)).find((element) => !isPhoebeOwnedElement(container, element)) ??
    null
  );
}

function directHostBodyChildren(container: HTMLElement): Element[] {
  return Array.from(document.body.children).filter((element) => !isPhoebeOwnedElement(container, element));
}

function hostBodyChildForElement(container: HTMLElement, element: Element): Element | null {
  let current: Element | null = element;
  while (current?.parentElement && current.parentElement !== document.body) {
    current = current.parentElement;
  }

  if (current?.parentElement === document.body && !isPhoebeOwnedElement(container, current)) {
    return current;
  }
  return null;
}

function hostHeaderBoundaryBodyChildren(container: HTMLElement): Element[] {
  return directHostBodyChildren(container).filter(isHostHeaderBoundaryElement);
}

function hasHostHeaderBoundaryBefore(element: Element, container: HTMLElement): boolean {
  return hostHeaderBoundaryBodyChildren(container).some((candidate) =>
    Boolean(candidate.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING),
  );
}

function insertChatRoot(container: HTMLDivElement): ChatRootPlacement {
  const mainContent = firstHostElement(container, "main, [role='main']");
  const mainBodyChild = mainContent ? hostBodyChildForElement(container, mainContent) : null;
  if (mainBodyChild && hasHostHeaderBoundaryBefore(mainBodyChild, container)) {
    if (container.parentNode !== document.body || container.nextSibling !== mainBodyChild) {
      document.body.insertBefore(container, mainBodyChild);
    }
    return "before-main";
  }

  const hostHeaderBoundaries = hostHeaderBoundaryBodyChildren(container);
  const hostHeader = hostHeaderBoundaries[hostHeaderBoundaries.length - 1];
  if (hostHeader?.parentNode) {
    if (container.parentNode !== document.body || hostHeader.nextSibling !== container) {
      document.body.insertBefore(container, hostHeader.nextSibling);
    }
    return "after-header";
  }
  if (container.parentNode !== document.body || container.nextSibling) {
    document.body.appendChild(container);
  }
  return "body";
}

function keepChatRootNearHostHeader(container: HTMLDivElement) {
  if (insertChatRoot(container) === "before-main") return;

  const observer = new MutationObserver(() => {
    if (insertChatRoot(container) === "before-main") {
      observer.disconnect();
    }
  });

  observer.observe(document.body, { childList: true });
  window.setTimeout(() => observer.disconnect(), HOST_LAYOUT_OBSERVER_TIMEOUT_MS);
}

async function mount() {
  if (DEBUG) console.log("[phoebe-chat] mount() called — readyState:", document.readyState);
  const config = window.__PHOEBE_CONFIG__ ?? {};
  const hasExplicitEnabled = config.enabled !== undefined;

  // Avoid a second flag request when a host injects the bundle more than once
  // after the first mount has completed. Explicit overrides remain synchronous
  // and are evaluated before the shared async guard below.
  if (document.getElementById("phoebe-chat-root")) {
    console.warn("[phoebe-chat] already mounted — skipping");
    return;
  }

  if (!hasExplicitEnabled && window.__PHOEBE_MOUNT_IN_FLIGHT__) {
    if (DEBUG) console.log("[phoebe-chat] mount() already in flight — skipping concurrent call");
    return;
  }
  if (!hasExplicitEnabled) window.__PHOEBE_MOUNT_IN_FLIGHT__ = true;

  try {
    // window.__PHOEBE_CONFIG__.backendUrl takes precedence; fall back to CDN-inferred URL.
    const backendUrl = resolveBackendUrl(config.backendUrl, CURRENT_SCRIPT_SRC);

    // Explicit override always wins and stays fully synchronous — no network
    // call is made when the host page sets `enabled` to a boolean explicitly
    // (required for local dev and existing Playwright tests to run without a
    // live backend). Any other non-boolean value (e.g. a stray string) is
    // treated as "not configured" but does NOT trigger the flag fetch either —
    // only a true `undefined` does, matching the retired hostname-fallback's
    // exact contract for non-boolean inputs.
    if (config.enabled !== true) {
      if (config.enabled !== undefined) return;
      const enabled = await resolveLauncherEnabledOnce(backendUrl);
      const latestConfig = window.__PHOEBE_CONFIG__ ?? {};
      if (latestConfig.enabled !== undefined) {
        if (latestConfig.enabled !== true) return;
      } else if (!enabled) {
        return;
      }
    }

    if (DEBUG) {
      console.log("[phoebe-chat] config:", {
        backendUrl: backendUrl ?? "(none)",
        sessionIdentity: config.sessionIdentity ? "configured" : "(none)",
      });
    }

    // Nonprod and production artifacts resolve this release gate through the
    // backend's shared GrowthBook/OpenFeature provider. The browser receives
    // only the allowlisted boolean, never a GrowthBook client key.
    const [sidePanelShellEnabled, genesysLiveChatEnabled] = IIFE_STRICT_SIDE_PANEL_POLICY
      ? await Promise.all([
          resolveSidePanelEnabled(backendUrl, { debug: DEBUG }),
          config.genesysLiveChatEnabled ?? resolveLiveChatEnabled(backendUrl, { debug: DEBUG }),
        ])
      : [config.sidePanelShellEnabled, config.genesysLiveChatEnabled];

    // A second invocation may have mounted while the flag request was pending
    // (for example, an explicit local override arriving from a host script).
    // Recheck before initialising analytics or replacing an active root.
    if (document.getElementById("phoebe-chat-root")) {
      console.warn("[phoebe-chat] already mounted — skipping");
      return;
    }
    if (window.__PHOEBE_CHAT_ROOT__) {
      try {
        window.__PHOEBE_CHAT_ROOT__.unmount();
      } catch {
        // Best-effort cleanup for host pages that replace document contents.
      }
      delete window.__PHOEBE_CHAT_ROOT__;
    }

    // Initialise analytics after the double-mount guard so a second invocation
    // cannot call disable() or initFromWindow() against an already-running instance.
    if (config.disableAnalytics) {
      analytics.disable();
    } else {
      analytics.initFromWindow(config.segmentWriteKey);
    }

    const container = document.createElement("div");
    container.id = "phoebe-chat-root";
    container.style.position = "relative";
    keepChatRootNearHostHeader(container);
    if (DEBUG) console.log("[phoebe-chat] container inserted, rendering ChatModal...");

    // Merge top-level userToken into initialIdentity.userToken when the host
    // page supplies it and sessionIdentity.userToken is not already set.
    // Use (config.sessionIdentity ?? {}) so the spread is explicit and safe when
    // the host page omits sessionIdentity entirely.
    let initialIdentity = config.sessionIdentity;
    if (config.userToken && !config.sessionIdentity?.userToken) {
      initialIdentity = { ...(config.sessionIdentity ?? {}), userToken: config.userToken };
    }

    const root = createRoot(container);
    window.__PHOEBE_CHAT_ROOT__ = root;
    root.render(
      React.createElement(ChatModal, {
        backendUrl,
        silentWidgetAuth: config.silentWidgetAuth === true,
        initialIdentity,
        guestBootstrap: resolveGuestBootstrapOption({
          backendUrl,
          disableGuestBootstrap: config.disableGuestBootstrap,
          enableGuestBootstrap: config.enableGuestBootstrap,
        }),
        layout: config.layout,
        sidePanelShellEnabled,
        strictSidePanelPolicy: IIFE_STRICT_SIDE_PANEL_POLICY,
        enrollmentConsultationEnabled: config.enrollmentConsultationEnabled,
        thinkingInsightsEnabled: config.thinkingInsightsEnabled,
        includeMessageBody: config.includeMessageBody,
        // QA needs the console utilities on the sandbox and stage embeds without
        // a GTM tag change; prod embeds stay opt-in only (VIAF-455).
        enableDevtools: config.enableDevtools ?? isNonProdCdnScriptSrc(CURRENT_SCRIPT_SRC),
        genesysLiveChatEnabled,
        genesysLiveChatQueueDisplayEnabled: config.genesysLiveChatQueueDisplayEnabled,
        growthBookClientKey: IIFE_STRICT_SIDE_PANEL_POLICY ? undefined : config.growthBookClientKey,
        growthBookApiHost: IIFE_STRICT_SIDE_PANEL_POLICY ? undefined : config.growthBookApiHost,
      }),
    );
    if (DEBUG) console.log("[phoebe-chat] ChatModal rendered ✓");
  } catch (err) {
    console.error("[phoebe-chat] mount error:", err);
    // Show visible error banner so problems are obvious during dev
    const banner = document.createElement("div");
    banner.style.cssText =
      "position:fixed;bottom:0;right:0;background:#c00;color:#fff;" +
      "padding:12px 16px;font:14px monospace;z-index:99999;max-width:400px;white-space:pre-wrap";
    banner.textContent = "[phoebe-chat] " + String(err);
    document.body?.appendChild(banner);
  } finally {
    if (!hasExplicitEnabled) window.__PHOEBE_MOUNT_IN_FLIGHT__ = false;
  }
}

function resolveLauncherEnabledOnce(backendUrl: string | null | undefined): Promise<boolean> {
  const cached = window.__PHOEBE_LAUNCHER_FLAG_PROMISE__;
  if (cached?.backendUrl === backendUrl) return cached.promise;

  const decision = resolveLauncherEnabled(backendUrl, { debug: DEBUG });
  window.__PHOEBE_LAUNCHER_FLAG_PROMISE__ = { backendUrl, promise: decision };
  return decision;
}

if (document.readyState === "loading") {
  if (DEBUG) console.log("[phoebe-chat] waiting for DOMContentLoaded...");
  document.addEventListener("DOMContentLoaded", mount);
} else {
  if (DEBUG) console.log("[phoebe-chat] DOM already ready, mounting immediately");
  mount();
}

// Surface unhandled errors originating from this bundle specifically.
// Using the full bundle filename pattern avoids false matches on host-page scripts.
window.addEventListener("error", (e) => {
  if (
    e.filename?.includes("phoebe-chat.global") ||
    e.error?.stack?.includes("phoebe-chat.global")
  ) {
    console.error("[phoebe-chat] uncaught error:", e.error);
  }
});
